import React, { useState, useEffect, useContext } from "react";
import { useApiClient } from "../utils/apiClient";
import { AuthContext } from "../contexts/AuthContext";
import { v4 as uuidv4 } from "uuid";
import { XMarkIcon, PlusIcon, ChevronDownIcon, ChevronRightIcon, TrashIcon, PencilIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

const extractDegreeFromName = (name) => {
    if (!name) return { nameWithoutDegree: name, degree: null };
    const nameParts = name.trim().split(' ');
    if (nameParts.length < 2) return { nameWithoutDegree: name, degree: null };
    
    const lastWord = nameParts[nameParts.length - 1].toUpperCase();
    const commonDegrees = ['MD', 'CRNA', 'RN', 'NP', 'PA', 'DO', 'DDS', 'DMD', 'PhD', 'DNP', 'MSN', 'BSN', 'LPN', 'CNA'];
    
    if (commonDegrees.includes(lastWord)) {
        const nameWithoutDegree = nameParts.slice(0, -1).join(' ');
        return { nameWithoutDegree, degree: lastWord };
    }
    
    return { nameWithoutDegree: name, degree: null };
};

const groupProvidersByDegree = (providers) => {
    const grouped = {};
    
    providers.forEach(provider => {
        const { degree } = extractDegreeFromName(provider.user_name || provider.email || provider.user_id);
        const degreeKey = degree || 'Other';
        
        if (!grouped[degreeKey]) {
            grouped[degreeKey] = [];
        }
        grouped[degreeKey].push(provider);
    });
    
    Object.keys(grouped).forEach(degree => {
        grouped[degree].sort((a, b) => {
            const aName = (a.user_name || a.email || a.user_id || '').toLowerCase();
            const bName = (b.user_name || b.email || b.user_id || '').toLowerCase();
            return aName.localeCompare(bName);
        });
    });
    
    return grouped;
};

export default function Centers() {
    const apiClient = useApiClient();
    const [centers, setCenters] = useState([]);
    const [expandedCenters, setExpandedCenters] = useState(new Set());
    const [centerRooms, setCenterRooms] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editCenter, setEditCenter] = useState(null); // {center, rooms}
    const [sortField, setSortField] = useState('type');
    const [sortDirection, setSortDirection] = useState('asc');
    const [form, setForm] = useState({
        name: "",
        location: "",
        preference: "",
        type: "medical_center",
        rooms: [],
        roomName: "",
        certified_providers: [],
    });
    const [editForm, setEditForm] = useState({
        name: "",
        location: "",
        preference: "",
        type: "medical_center",
        rooms: [],
        roomName: "",
        certified_providers: [],
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [providers, setProviders] = useState([]);
    const [providerSearchAdd, setProviderSearchAdd] = useState("");
    const [providerSearchEdit, setProviderSearchEdit] = useState("");
    const [deleteAssignmentsModalOpen, setDeleteAssignmentsModalOpen] = useState(false);
    const [selectedCenterForDeletion, setSelectedCenterForDeletion] = useState(null);
    const [deleteAssignmentsForm, setDeleteAssignmentsForm] = useState({
        month: new Date().toISOString().slice(0, 7)
    });

    useEffect(() => {
        fetchCenters();
        fetchProviders();
    }, []);

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                setSuccess("");
            }, 15000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    async function fetchCenters() {
        setLoading(true);
        setError("");
        try {
            const resp = await apiClient.get("/centers");
            setCenters(resp.centers || []);
        } catch (e) {
            setError("Failed to load facilities");
        } finally {
            setLoading(false);
        }
    }

    async function fetchProviders() {
        try {
            const resp = await apiClient.get("/user_profile", { params: { role: "provider" } });
            setProviders(resp.users || []);
        } catch (e) {
            console.error("Failed to load providers:", e);
        }
    }

    function handleSort(field) {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    }

    function getSortedCenters() {
        return [...centers].sort((a, b) => {
            let aValue, bValue;
            
            if (sortField === 'city') {
                aValue = getCityFromLocation(a.location || '').toLowerCase();
                bValue = getCityFromLocation(b.location || '').toLowerCase();
            } else if (sortField === 'type') {
                const aType = a.type || 'medical_center';
                const bType = b.type || 'medical_center';
                
                if (aType === 'hospital' && bType !== 'hospital') return -1;
                if (bType === 'hospital' && aType !== 'hospital') return 1;
                
                return (a.name || '').localeCompare(b.name || '');
            } else {
                aValue = (a[sortField] || '').toLowerCase();
                bValue = (b[sortField] || '').toLowerCase();
            }
            
            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }

    function highlightText(text, searchTerm) {
        if (!searchTerm) return text;
        const regex = new RegExp(`(${searchTerm})`, 'gi');
        return text.replace(regex, '<mark class="bg-yellow-200 px-1 rounded">$1</mark>');
    }

    function getFilteredProviders(searchTerm) {
        if (!searchTerm) return providers;
        const lowerSearch = searchTerm.toLowerCase();
        return providers.filter(provider => {
            const name = (provider.user_name || '').toLowerCase();
            const email = (provider.email || '').toLowerCase();
            const id = (provider.user_id || '').toLowerCase();
            return name.includes(lowerSearch) || email.includes(lowerSearch) || id.includes(lowerSearch);
        });
    }

    function getCityFromLocation(location) {
        if (!location) return '';
        const parts = location.split(' ');
        const stateIndex = parts.findIndex(part => part === 'CA');
        if (stateIndex > 0) {
            if (stateIndex >= 2) {
                const secondToLast = parts[stateIndex - 2];
                if (secondToLast === 'Los' || secondToLast === 'San' || secondToLast === 'Santa') {
                    return `${parts[stateIndex - 2]} ${parts[stateIndex - 1]}`;
                }
            }
            return parts[stateIndex - 1] || '';
        }
        return location;
    }

    // LOCATION repeated the city and zip that CITY already shows on the same row,
    // which cost the width PREFERENCE needed.
    function getStreetFromLocation(location) {
        if (!location) return '';
        const parts = location.split(' ');
        const stateIndex = parts.findIndex(part => part === 'CA');
        if (stateIndex <= 0) return location;
        const cityWords = (stateIndex >= 2 && ['Los', 'San', 'Santa'].includes(parts[stateIndex - 2])) ? 2 : 1;
        return parts.slice(0, stateIndex - cityWords).join(' ') || location;
    }

    function getSortIcon(field) {
        if (sortField !== field) {
            return <ChevronUpIcon className="h-4 w-4 text-gray-400" />;
        }
        return sortDirection === 'asc' 
            ? <ChevronUpIcon className="h-4 w-4 text-blue-600" />
            : <ChevronDownIcon className="h-4 w-4 text-blue-600" />;
    }

    async function toggleCenterInfo(center) {
        const centerId = center.center_id;
        const isExpanded = expandedCenters.has(centerId);

        if (isExpanded) {
            setExpandedCenters(new Set());
        } else {
            if (!centerRooms[centerId]) {
                setLoading(true);
                setError("");
                try {
                    const resp = await apiClient.get(`/centers?center_id=${centerId}`);
                    const rooms = resp.center?.rooms || [];
                    setCenterRooms(prev => ({ ...prev, [centerId]: rooms }));
                } catch (e) {
                                setError("Failed to load facility info");
            setCenterRooms(prev => ({ ...prev, [centerId]: [] }));
                } finally {
                    setLoading(false);
                }
            }
            setExpandedCenters(new Set([centerId]));
        }
    }

    function handleFormChange(e) {
        const { name, value, type, checked } = e.target;
        setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    }

    function handleEditFormChange(e) {
        const { name, value, type, checked } = e.target;
        setEditForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    }

    function handleAddRoom() {
        const name = form.roomName.trim();
        if (!name) return;
        setForm((f) => ({
            ...f,
            rooms: f.rooms.includes(name) ? f.rooms : [...f.rooms, name],
            roomName: "",
        }));
    }

    function handleRemoveRoom(idx) {
        setForm((f) => ({
            ...f,
            rooms: f.rooms.filter((_, i) => i !== idx),
        }));
    }

    function handleEditAddRoom() {
        const name = editForm.roomName.trim();
        if (!name) return;
        setEditForm((f) => ({
            ...f,
            rooms: f.rooms.includes(name) ? f.rooms : [...f.rooms, name],
            roomName: "",
        }));
    }

    function handleEditRemoveRoom(idx) {
        setEditForm((f) => ({
            ...f,
            rooms: f.rooms.filter((_, i) => i !== idx),
        }));
    }



    async function handleAdd() {
        const errors = [];
        if (!form.name.trim()) {
            errors.push("Center name is required");
        }
        if (!form.location.trim()) {
            errors.push("Location is required");
        }

        if (errors.length > 0) {
            setError(errors.join(", "));
            return;
        }
        setLoading(true);
        setError("");
        setSuccess("");
        try {
            const center_id = uuidv4();
            await apiClient.post("/centers", {
                action: "add_center",
                center_id,
                name: form.name.trim(),
                location: form.location.trim() || "",
                preference: form.preference.trim() || "",
                type: form.type,
                certified_providers: form.certified_providers,
                rooms: form.rooms,
            });
            await fetchCenters();
            setModalOpen(false);
            setForm({
                name: "",
                location: "",
                preference: "",
                type: "medical_center",
                rooms: [],
                roomName: "",
                certified_providers: [],
            });
            setProviderSearchAdd("");
            setSuccess("Facility added successfully!");
        } catch (e) {
            setError("Failed to add facility");
        } finally {
            setLoading(false);
        }
    }

    async function handleEdit(center) {
        setLoading(true);
        setError("");
        setSuccess("");
        try {
            let rooms = centerRooms[center.center_id];
            if (!rooms) {
                const resp = await apiClient.get(`/centers?center_id=${center.center_id}`);
                rooms = resp.center?.rooms || [];
                setCenterRooms(prev => ({ ...prev, [center.center_id]: rooms }));
            }
            setEditCenter(center);
            setEditForm({
                name: center.name || "",
                location: center.location || "",
                preference: center.preference || "",
                type: center.type || "medical_center",
                rooms: rooms,
                roomName: "",
                certified_providers: center.certified_providers || [],
            });
            setProviderSearchEdit("");
            setEditModalOpen(true);
        } catch (e) {
            setError("Failed to load facility info");
        } finally {
            setLoading(false);
        }
    }

    async function handleEditSave() {
        const errors = [];
        if (!editForm.name.trim()) {
            errors.push("Center name is required");
        }
        if (!editForm.location.trim()) {
            errors.push("Location is required");
        }

        if (errors.length > 0) {
            setError(errors.join(", "));
            return;
        }
        setLoading(true);
        setError("");
        setSuccess("");
        try {
            await apiClient.post("/centers", {
                action: "update_center",
                center_id: editCenter.center_id,
                name: editForm.name.trim(),
                location: editForm.location.trim() || "",
                preference: editForm.preference || "",
                type: editForm.type,
                rooms: editForm.rooms,
                certified_providers: editForm.certified_providers,
            });
            setCenterRooms(prev => ({ ...prev, [editCenter.center_id]: editForm.rooms }));
            await fetchCenters();
            setEditModalOpen(false);
            setEditCenter(null);
            setProviderSearchEdit("");
            setSuccess("Facility updated successfully!");
        } catch (e) {
            setError("Failed to update facility");
        } finally {
            setLoading(false);
        }
    }

    async function handleDelete(center) {
        if (!window.confirm(`Are you sure you want to delete the facility "${center.name}"? This will also delete all its rooms.`)) {
            return;
        }
        setLoading(true);
        setError("");
        try {
            await apiClient.delete("/centers", {
                center_id: center.center_id,
            });
            await fetchCenters();
            setExpandedCenters(prev => {
                const newSet = new Set(prev);
                newSet.delete(center.center_id);
                return newSet;
            });
            setCenterRooms(prev => {
                const newRooms = { ...prev };
                delete newRooms[center.center_id];
                return newRooms;
            });
        } catch (e) {
            setError("Failed to delete facility");
        } finally {
            setLoading(false);
        }
    }

    function handleDeleteAssignments(center) {
        setSelectedCenterForDeletion(center);
        setDeleteAssignmentsForm({
            month: new Date().toISOString().slice(0, 7)
        });
        setDeleteAssignmentsModalOpen(true);
        setError("");
    }

    async function handleDeleteAssignmentsConfirm() {
        if (!selectedCenterForDeletion) return;
        
        const { month } = deleteAssignmentsForm;
        const [year, monthNum] = month.split('-').map(Number);
        const monthName = new Date(year, monthNum - 1).toLocaleString('default', { month: 'long' });
        
        if (!window.confirm(`Are you sure you want to delete ALL assignments for "${selectedCenterForDeletion.name}" in ${monthName} ${year}? This action cannot be undone.`)) {
            return;
        }
        
        setLoading(true);
        setError("");
        try {
            const response = await apiClient.post("/centers", {
                action: "delete_assignments_by_month",
                center_id: selectedCenterForDeletion.center_id,
                year: year,
                month: monthNum
            });
            
            setDeleteAssignmentsModalOpen(false);
            setSelectedCenterForDeletion(null);
            setSuccess(`Successfully deleted ${response.deleted_count} assignments for ${selectedCenterForDeletion.name} in ${monthName} ${year}`);
        } catch (e) {
            setError("Failed to delete assignments");
        } finally {
            setLoading(false);
        }
    }

    function convertTo12HourFormat(time24) {
        if (!time24) return "";
        const [hours, minutes] = time24.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${hour12}:${minutes.padStart(2, '0')} ${ampm}`;
    }

    function convertTo24HourFormat(time12) {
        if (!time12) return "";
        try {
            const [time, period] = time12.split(' ');
            const [hours, minutes] = time.split(':');
            let hour = parseInt(hours);
            
            if (period === 'PM' && hour !== 12) {
                hour += 12;
            } else if (period === 'AM' && hour === 12) {
                hour = 0;
            }
            
            return `${hour.toString().padStart(2, '0')}:${minutes}`;
        } catch (e) {
            return "";
        }
    }


    const { user } = useContext(AuthContext);
    const isManagerActive = user?.activeRole === 'Manager';

    return (
        <div className="h-full flex flex-col px-6 py-4">
            <div className="flex justify-between items-center mb-4 shrink-0">
                <h1 className="sr-only">Facilities</h1>
                {isManagerActive && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => { setModalOpen(true); setError(""); setSuccess(""); }}
                            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                        >
                            <PlusIcon className="h-5 w-5" />
                            Add Facility
                        </button>
                        <button
                            onClick={() => { setDeleteAssignmentsModalOpen(true); setError(""); setDeleteAssignmentsForm({ month: new Date().toISOString().slice(0, 7) }); setSelectedCenterForDeletion((centers && centers.length > 0) ? centers[0] : null); }}
                            className="flex items-center gap-2 border border-red-300 text-red-700 px-4 py-2 rounded-md hover:bg-red-50 transition-colors"
                            disabled={centers.length === 0}
                        >
                            <TrashIcon className="h-5 w-5" />
                            Delete Assignments
                        </button>
                    </div>
                )}
            </div>

            {error && (
                <div className="mb-4 p-3 shrink-0 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                </div>
            )}

            {success && (
                <div className="mb-4 p-3 shrink-0 bg-green-100 border border-green-400 text-green-700 rounded flex justify-between items-center">
                    <span>{success}</span>
                    <button
                        onClick={() => setSuccess("")}
                        className="text-green-700 hover:text-green-900"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
            )}

            {loading ? (
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-gray-600">Loading facilities...</p>
                </div>
            ) : (
                <div className="flex-1 min-h-0 flex flex-col">
                    <div className="flex-1 min-h-0 overflow-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0 z-10 [&_th]:shadow-[inset_0_-1px_0_#e5e7eb]">
                                <tr>
                                    <th 
                                        className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                        onClick={() => handleSort('name')}
                                    >
                                        <div className="flex items-center gap-1">
                                            Facility Name
                                            {getSortIcon('name')}
                                        </div>
                                    </th>
                                    <th 
                                        className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                        onClick={() => handleSort('location')}
                                    >
                                        <div className="flex items-center gap-1">
                                            Location
                                            {getSortIcon('location')}
                                        </div>
                                    </th>
                                    <th 
                                        className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                        onClick={() => handleSort('type')}
                                    >
                                        <div className="flex items-center gap-1">
                                            Type
                                            {getSortIcon('type')}
                                        </div>
                                    </th>
                                    <th 
                                        className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                        onClick={() => handleSort('city')}
                                    >
                                        <div className="flex items-center gap-1">
                                            City
                                            {getSortIcon('city')}
                                        </div>
                                    </th>
                                    <th 
                                        className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                        onClick={() => handleSort('preference')}
                                    >
                                        <div className="flex items-center gap-1">
                                            Preference
                                            {getSortIcon('preference')}
                                        </div>
                                    </th>
                                    <th className="w-1"></th>
                                    <th className="w-1"></th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {centers.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-2 text-center text-gray-500">
                                            No facilities found
                                        </td>
                                    </tr>
                                ) : (
                                    getSortedCenters().map((center, idx) => {
                                        const isExpanded = expandedCenters.has(center.center_id);
                                        const rooms = centerRooms[center.center_id] || [];
                                        const city = getCityFromLocation(center.location);

                                        return (
                                            <React.Fragment key={idx}>
                                                <tr className="hover:bg-gray-50">
                                                    <td className="px-6 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {center.name}
                                                    </td>
                                                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
                                                        {getStreetFromLocation(center.location) || "—"}
                                                    </td>
                                                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
                                                        {center.type ? center.type.split("_").map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(" ") : "—"}
                                                    </td>
                                                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
                                                        {city}
                                                    </td>
                                                    <td className="px-6 py-2 text-sm text-gray-500 w-2/5 max-w-0">
                                                        <div className="truncate" title={center.preference || ""}>
                                                            {center.preference || "—"}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                                        {isManagerActive && (
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={() => handleEdit(center)}
                                                                    className="text-gray-400 hover:text-blue-600 transition-colors flex items-center gap-1"
                                                                    disabled={loading}
                                                                    title="Edit facility"
                                                                >
                                                                    <PencilIcon className="h-4 w-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDelete(center)}
                                                                    className="text-gray-400 hover:text-red-600 transition-colors flex items-center gap-1"
                                                                    disabled={loading}
                                                                    title="Delete facility"
                                                                >
                                                                    <TrashIcon className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-right">
                                                        <button
                                                            onClick={() => toggleCenterInfo(center)}
                                                            className="flex items-center gap-1 text-blue-600 hover:text-blue-900 font-medium transition-colors"
                                                            title={isExpanded ? "Hide center info" : "View center info"}
                                                        >
                                                            {isExpanded ? (
                                                                <ChevronDownIcon className="h-4 w-4" />
                                                            ) : (
                                                                <ChevronRightIcon className="h-4 w-4" />
                                                            )}
                                                        </button>
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan="8" className="px-0 py-0 bg-gray-50">
                                                            <div className="px-6 py-4 transition-all duration-300 ease-in-out">
                                                                <div className="bg-white rounded-lg border border-gray-200 p-4">
                                                                    <h3 className="text-sm font-medium text-gray-900 mb-3">
                                                                        Center Information for {center.name}
                                                                    </h3>
                                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                                        <div>
                                                                            <h4 className="text-sm font-medium text-gray-700 mb-2">Rooms:</h4>
                                                                            {rooms.length === 0 ? (
                                                                                <div className="text-gray-500 text-sm">No rooms found.</div>
                                                                            ) : (
                                                                                <div className="flex flex-wrap gap-2">
                                                                                    {rooms.map((room, roomIdx) => (
                                                                                        <span
                                                                                            key={roomIdx}
                                                                                            className="inline-flex items-center px-3 py-1 text-sm rounded-full border bg-gray-100 text-gray-700 border-gray-200"
                                                                                        >
                                                                                            {room}
                                                                                        </span>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div className="mr-8 max-w-md">
                                                                            <h4 className="text-sm font-medium text-gray-700 mb-2">Center Details:</h4>
                                                                            <div className="space-y-1 text-sm text-gray-600">
                                                                                <p><span className="font-medium">Location:</span> {center.location || "Not specified"}</p>
                                                                                <p><span className="font-medium">Preference:</span></p>
                                                                                {center.preference ? (
                                                                                    <div className="ml-4">
                                                                                        <pre className="whitespace-pre-wrap text-sm text-gray-600 font-normal">
                                                                                            {center.preference}
                                                                                        </pre>
                                                                                    </div>
                                                                                ) : (
                                                                                    <p className="ml-4 text-gray-500">Not specified</p>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        <div className="ml-4">
                                                                            <h4 className="text-sm font-medium text-gray-700 mb-2">Certified Providers: (Hover to see preferences)</h4>
                                                                            {center.certified_providers && center.certified_providers.length > 0 ? (
                                                                                <div className="flex flex-wrap gap-2">
                                                                                    {center.certified_providers.map((providerId, providerIdx) => {
                                                                                        const provider = providers.find(p => p.user_id === providerId);
                                                                                        const providerName = provider ? (provider.user_name || provider.email || provider.user_id) : providerId;
                                                                                        const preferences = provider?.preferences || {};
                                                                                        const availability = provider?.availability || [];
                                                                                        const unavailability = provider?.unavailability || [];
                                                                                        
                                                                                        return (
                                                                                            <div key={providerIdx} className="relative group">
                                                                                                <span className="inline-flex items-center px-3 py-1 text-sm rounded-full border bg-blue-100 text-blue-700 border-blue-200 cursor-help">
                                                                                                    {providerName}
                                                                                                </span>
                                                                                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 w-64">
                                                                                                    <div className="text-center font-medium mb-2">{providerName}</div>
                                                                                                    {preferences && Object.keys(preferences).length > 0 ? (
                                                                                                        <div>
                                                                                                            <div className="font-semibold mb-1">Preferences:</div>
                                                                                                            <div className="text-gray-300 text-xs whitespace-pre-wrap">
                                                                                                                {typeof preferences === 'string' ? preferences : JSON.stringify(preferences, null, 2)}
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    ) : (
                                                                                                        <div className="text-gray-400 text-xs">No preferences set</div>
                                                                                                    )}
                                                                                                    {availability && availability.length > 0 && (
                                                                                                        <div className="mt-2">
                                                                                                            <div className="font-semibold mb-1">Availability:</div>
                                                                                                            <div className="text-gray-300 text-xs whitespace-pre-wrap">
                                                                                                                {Array.isArray(availability) ? availability.join('\n') : availability}
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    )}
                                                                                                    {unavailability && unavailability.length > 0 && (
                                                                                                        <div className="mt-2">
                                                                                                            <div className="font-semibold mb-1">Unavailability:</div>
                                                                                                            <div className="text-gray-300 text-xs whitespace-pre-wrap">
                                                                                                                {Array.isArray(unavailability) ? unavailability.join('\n') : unavailability}
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    )}
                                                                                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                                                                </div>
                                                                                            </div>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            ) : (
                                                                                <div className="text-gray-500 text-sm">No certified providers assigned.</div>
                                                                            )}
                                                                           
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {editModalOpen && isManagerActive && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="bg-white w-full max-w-7xl max-h-[90vh] rounded shadow relative flex flex-col">
                        <div className="flex-shrink-0 p-6 border-b border-gray-200">
                            <button
                                className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100"
                                onClick={() => { setEditModalOpen(false); setEditCenter(null); setProviderSearchEdit(""); setSuccess(""); setError(""); }}
                            >
                                <XMarkIcon className="h-6 w-6 text-gray-500" />
                            </button>
                            <h2 className="text-xl font-bold">Edit Facility</h2>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-6">
                            {error && (
                                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                                    {error}
                                </div>
                            )}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Center Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={editForm.name}
                                        onChange={handleEditFormChange}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter center name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Location</label>
                                    <input
                                        type="text"
                                        name="location"
                                        value={editForm.location}
                                        onChange={handleEditFormChange}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter center location"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Preference</label>
                                    <textarea
                                        name="preference"
                                        value={editForm.preference}
                                        onChange={handleEditFormChange}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                                        placeholder="Enter center preference"
                                        rows="3"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Center Type</label>
                                    <select
                                        name="type"
                                        value={editForm.type}
                                        onChange={handleEditFormChange}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="medical_center">Medical Center</option>
                                        <option value="hospital">Hospital</option>
                                    </select>
                                </div>
                                <div>
                                     <label className="block text-sm font-medium mb-1">Rooms</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            name="roomName"
                                            value={editForm.roomName}
                                            onChange={handleEditFormChange}
                                            className="flex-1 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Room name"
                                        />

                                        <button
                                            type="button"
                                            onClick={handleEditAddRoom}
                                            className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                        >
                                            Add
                                        </button>
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {editForm.rooms.map((room, idx) => (
                                            <span key={idx} className="inline-flex items-center px-2 py-1 text-xs rounded border bg-gray-100 text-gray-500 border-gray-200">
                                                {room}
                                                <button
                                                    type="button"
                                                    className="ml-1 text-red-500 hover:text-red-700"
                                                    onClick={() => handleEditRemoveRoom(idx)}
                                                    title="Remove room"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Certified Providers</label>
                                    <div className="mb-2">
                                        <input
                                            type="text"
                                            placeholder="Search providers..."
                                            value={providerSearchEdit}
                                            onChange={(e) => setProviderSearchEdit(e.target.value)}
                                            className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div className="border rounded p-3 min-h-[100px] max-h-[200px] overflow-y-auto">
                                        {(() => {
                                            const filtered = getFilteredProviders(providerSearchEdit);
                                            const grouped = groupProvidersByDegree(filtered);
                                            const degreeOrder = ['MD', 'DO', 'CRNA', 'NP', 'PA', 'RN', 'DDS', 'DMD', 'PhD', 'DNP', 'MSN', 'BSN', 'LPN', 'CNA', 'Other'];
                                            
                                            return degreeOrder.filter(degree => grouped[degree]).map(degree => (
                                                <div key={degree} className="mb-3 last:mb-0">
                                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                                        {degree}
                                                    </div>
                                                    <div className="space-y-1">
                                                        {grouped[degree].map(provider => {
                                                            const isSelected = editForm.certified_providers.includes(provider.user_id);
                                                            const { nameWithoutDegree } = extractDegreeFromName(provider.user_name || provider.email || provider.user_id);
                                                            return (
                                                                <div
                                                                    key={provider.user_id}
                                                                    className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-50 ${
                                                                        isSelected ? 'bg-blue-50 border border-blue-200' : ''
                                                                    }`}
                                                                    onClick={() => {
                                                                        if (isSelected) {
                                                                            setEditForm(prev => ({
                                                                                ...prev,
                                                                                certified_providers: prev.certified_providers.filter(id => id !== provider.user_id)
                                                                            }));
                                                                        } else {
                                                                            setEditForm(prev => ({
                                                                                ...prev,
                                                                                certified_providers: [...prev.certified_providers, provider.user_id]
                                                                            }));
                                                                        }
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        onChange={() => {}}
                                                                        className="mr-2"
                                                                    />
                                                                    <span 
                                                                        className={isSelected ? 'font-medium text-blue-700' : 'text-gray-700'}
                                                                        dangerouslySetInnerHTML={{ 
                                                                            __html: highlightText(nameWithoutDegree, providerSearchEdit) 
                                                                        }}
                                                                    />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                    <p className="mt-1 text-sm text-gray-500">Click on providers to select/deselect them</p>
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {editForm.certified_providers.map((providerId, idx) => {
                                            const provider = providers.find(p => p.user_id === providerId);
                                            return (
                                                <span key={idx} className="inline-flex items-center px-2 py-1 text-xs rounded border bg-blue-100 text-blue-700 border-blue-200">
                                                    {provider ? (provider.user_name || provider.email || provider.user_id) : providerId}
                                                    <button
                                                        type="button"
                                                        className="ml-1 text-blue-500 hover:text-blue-700"
                                                        onClick={() => setEditForm(prev => ({ 
                                                            ...prev, 
                                                            certified_providers: prev.certified_providers.filter((_, i) => i !== idx) 
                                                        }))}
                                                        title="Remove provider"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2 mt-6">
                                    <button
                                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded border-none"
                                        style={{ minWidth: '80px', height: 'auto' }}
                                        onClick={() => { setEditModalOpen(false); setEditCenter(null); }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded border-none"
                                        style={{ minWidth: '80px', height: 'auto' }}
                                        onClick={handleEditSave}
                                        disabled={loading}
                                    >
                                        {loading ? "Saving..." : "Save Changes"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {modalOpen && isManagerActive && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="bg-white w-full max-w-7xl max-h-[90vh] rounded shadow relative flex flex-col">
                        <div className="flex-shrink-0 p-6 border-b border-gray-200">
                            <button
                                className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100"
                                onClick={() => { setModalOpen(false); setError(""); setSuccess(""); }}
                            >
                                <XMarkIcon className="h-6 w-6 text-gray-500" />
                            </button>
                            <h2 className="text-xl font-bold">Add Medical Center</h2>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-6">
                            {error && (
                                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                                    {error}
                                </div>
                            )}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Center Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={form.name}
                                        onChange={handleFormChange}
                                        required
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter center name"
                                    />
                                    {!form.name.trim() && (
                                        <p className="mt-1 text-sm text-red-600">Center name is required</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Location</label>
                                    <input
                                        type="text"
                                        name="location"
                                        value={form.location}
                                        onChange={handleFormChange}
                                        required
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter center location"
                                    />
                                    {!form.location.trim() && (
                                        <p className="mt-1 text-sm text-red-600">Location is required</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Preference</label>
                                    <textarea
                                        name="preference"
                                        value={form.preference}
                                        onChange={handleFormChange}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                                        placeholder="Enter center preference"
                                        rows="3"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Facility Type</label>
                                    <select
                                        name="type"
                                        value={form.type}
                                        onChange={handleFormChange}
                                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="medical_center">Medical Center</option>
                                        <option value="hospital">Hospital</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Rooms</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            name="roomName"
                                            value={form.roomName}
                                            onChange={handleFormChange}
                                            className="flex-1 border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Room name"
                                        />

                                        <button
                                            type="button"
                                            onClick={handleAddRoom}
                                            className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                        >
                                            Add
                                        </button>
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {form.rooms.map((room, idx) => (
                                            <span key={idx} className="inline-flex items-center px-2 py-1 text-xs rounded border bg-gray-100 text-gray-500 border-gray-200">
                                                {room}
                                                <button
                                                    type="button"
                                                    className="ml-1 text-red-500 hover:text-red-700"
                                                    onClick={() => handleRemoveRoom(idx)}
                                                    title="Remove room"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Certified Providers</label>
                                    <div className="mb-2">
                                        <input
                                            type="text"
                                            placeholder="Search providers..."
                                            value={providerSearchAdd}
                                            onChange={(e) => setProviderSearchAdd(e.target.value)}
                                            className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div className="border rounded p-3 min-h-[100px] max-h-[200px] overflow-y-auto">
                                        {(() => {
                                            const filtered = getFilteredProviders(providerSearchAdd);
                                            const grouped = groupProvidersByDegree(filtered);
                                            const degreeOrder = ['MD', 'DO', 'CRNA', 'NP', 'PA', 'RN', 'DDS', 'DMD', 'PhD', 'DNP', 'MSN', 'BSN', 'LPN', 'CNA', 'Other'];
                                            
                                            return degreeOrder.filter(degree => grouped[degree]).map(degree => (
                                                <div key={degree} className="mb-3 last:mb-0">
                                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                                        {degree}
                                                    </div>
                                                    <div className="space-y-1">
                                                        {grouped[degree].map(provider => {
                                                            const isSelected = form.certified_providers.includes(provider.user_id);
                                                            const { nameWithoutDegree } = extractDegreeFromName(provider.user_name || provider.email || provider.user_id);
                                                            return (
                                                                <div
                                                                    key={provider.user_id}
                                                                    className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-50 ${
                                                                        isSelected ? 'bg-blue-50 border border-blue-200' : ''
                                                                    }`}
                                                                    onClick={() => {
                                                                        if (isSelected) {
                                                                            setForm(prev => ({
                                                                                ...prev,
                                                                                certified_providers: prev.certified_providers.filter(id => id !== provider.user_id)
                                                                            }));
                                                                        } else {
                                                                            setForm(prev => ({
                                                                                ...prev,
                                                                                certified_providers: [...prev.certified_providers, provider.user_id]
                                                                            }));
                                                                        }
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        onChange={() => {}}
                                                                        className="mr-2"
                                                                    />
                                                                    <span 
                                                                        className={isSelected ? 'font-medium text-blue-700' : 'text-gray-700'}
                                                                        dangerouslySetInnerHTML={{ 
                                                                            __html: highlightText(nameWithoutDegree, providerSearchAdd) 
                                                                        }}
                                                                    />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                    <p className="mt-1 text-sm text-gray-500">Click on providers to select/deselect them</p>
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {form.certified_providers.map((providerId, idx) => {
                                            const provider = providers.find(p => p.user_id === providerId);
                                            return (
                                                <span key={idx} className="inline-flex items-center px-2 py-1 text-xs rounded border bg-blue-100 text-blue-700 border-blue-200">
                                                    {provider ? (provider.user_name || provider.email || provider.user_id) : providerId}
                                                    <button
                                                        type="button"
                                                        className="ml-1 text-blue-500 hover:text-blue-700"
                                                        onClick={() => setForm(prev => ({ 
                                                            ...prev, 
                                                            certified_providers: prev.certified_providers.filter((_, i) => i !== idx) 
                                                        }))}
                                                        title="Remove provider"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>

                            </div>
                            <div className="flex justify-end gap-2 mt-6">
                                <button
                                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded border-none"
                                    style={{ minWidth: '80px', height: 'auto' }}
                                    onClick={() => { setModalOpen(false); setError(""); }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded border-none"
                                    style={{ minWidth: '80px', height: 'auto' }}
                                    onClick={handleAdd}
                                    disabled={loading}
                                >
                                    {loading ? "Adding..." : "Add Facility"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {deleteAssignmentsModalOpen && isManagerActive && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="bg-white w-full max-w-md p-6 rounded shadow relative">
                        <button
                            className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100"
                            onClick={() => { setDeleteAssignmentsModalOpen(false); setSelectedCenterForDeletion(null); setError(""); }}
                        >
                            <XMarkIcon className="h-6 w-6 text-gray-500" />
                        </button>
                        <h2 className="text-xl font-bold mb-4">Delete Assignments</h2>
                        <div className="space-y-4">
                            <div className="bg-red-50 border border-red-200 rounded-md p-3">
                                <p className="text-sm text-red-800">
                                    <strong>Warning:</strong> This will permanently delete ALL assignments for the selected facility in the chosen month. This action cannot be undone.
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Facility</label>
                                <select
                                    value={selectedCenterForDeletion?.center_id || ""}
                                    onChange={(e) => {
                                        const selected = centers.find(c => c.center_id === e.target.value);
                                        setSelectedCenterForDeletion(selected || null);
                                    }}
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                                >
                                    <option value="" disabled>Select a facility</option>
                                    {getSortedCenters().map(c => (
                                        <option key={c.center_id} value={c.center_id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Month</label>
                                <input
                                    type="month"
                                    value={deleteAssignmentsForm.month}
                                    onChange={(e) => setDeleteAssignmentsForm(prev => ({ ...prev, month: e.target.value }))}
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                                />
                            </div>
                            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                                <p className="text-sm text-yellow-800">
                                    <strong>Note:</strong> Only assignments for the selected facility will be deleted. Assignments for other facilities will remain unchanged.
                                </p>
                            </div>
                            <div className="flex justify-end gap-2 mt-6">
                                <button
                                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded border-none"
                                    onClick={() => { setDeleteAssignmentsModalOpen(false); setSelectedCenterForDeletion(null); }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded border-none"
                                    onClick={handleDeleteAssignmentsConfirm}
                                    disabled={loading || !selectedCenterForDeletion}
                                >
                                    {loading ? "Deleting..." : "Delete Assignments"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
} 